import { IMiroBoardColor, IMiroJobResult, MIRO_MAX_BOARD_MOODBOARD_COLORS, MIRO_MAX_BOARD_MOODBOARD_IMAGES, MIRO_MAX_BOARD_MOODBOARD_NAME } from "api/miro/miro.type";
import { miroMessageKey } from "utils/miro";

/** The name a moodboard made from a board starts with: the frame's title, else the board's name (the BE's own default), capped at what it accepts. */
export const defaultBoardMoodboardName = (boardName: string, frameTitle?: string | null): string => (frameTitle?.trim() || boardName).slice(0, MIRO_MAX_BOARD_MOODBOARD_NAME);

/** More images than the BE takes — said BEFORE starting, not after a 422. */
export const exceedsBoardMoodboardCap = (imageCount: number): boolean => imageCount > MIRO_MAX_BOARD_MOODBOARD_IMAGES;

/**
 * Translation key for a refusal of the start route or a job-level `error_code`: this feature's own copy first
 * (`miro:board_moodboard.errors.*`), then the shared Miro one (`miro:errors.*` — connection, board gone, rate limit), else `null`
 * and the caller shows its generic line. Dynamic on purpose: the codes are the BE's vocabulary (`[I18N_KEY_MUST_EXIST]`).
 */
export const boardMoodboardErrorKey = (code: string | undefined, exists: (key: string) => boolean): string | null => {
  if (!code) return null;

  const own = `miro:board_moodboard.errors.${code.replace(/^miro\./, "")}`;

  return exists(own) ? own : miroMessageKey(code, "errors", exists);
};

/** What a settled `moodboard_from_board` job produced: a moodboard upload (hand off to My Moodboards), or nothing and why. */
export const boardMoodboardOutcome = (result: IMiroJobResult | null | undefined): { uploadId: string | null; split: boolean; images: number; included: number; code?: string } => ({
  uploadId: result?.upload?.job_id ?? null,
  split: result?.upload?.mode === "multi",
  images: result?.images ?? 0,
  included: result?.included ?? 0,
  code: result?.error_code,
});

/** The "being read, ready in about 10-15 minutes" line: moodboards (plural, with the small-theme warning) in Split-by-theme mode, one moodboard otherwise. */
export const boardMoodboardStartedKey = (split: boolean): string => (split ? "miro:board_moodboard.started_split" : "miro:board_moodboard.started");

const HEX = /^#[0-9a-f]{6}$/i;

/**
 * The colours a selection offers, de-duplicated by hex (first name wins), without anything that is not `#RRGGBB`, and at most what the
 * BE takes: what the chips show is exactly what is sent. `undefined` = the backend did not say.
 */
export const boardColorsOf = (colors: IMiroBoardColor[] | undefined): IMiroBoardColor[] | undefined => {
  if (!Array.isArray(colors)) return undefined;

  const seen = new Set<string>();

  return colors
    .filter((c) => {
      if (typeof c?.hex !== "string" || !HEX.test(c.hex)) return false;

      const key = c.hex.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);

      return true;
    })
    .slice(0, MIRO_MAX_BOARD_MOODBOARD_COLORS);
};

/** The `colors` of the start body: what the user kept, name only when there is one. Already capped by `boardColorsOf`; the slice is a backstop. */
export const boardColorsBody = (kept: IMiroBoardColor[]): { hex: string; name?: string }[] =>
  kept.slice(0, MIRO_MAX_BOARD_MOODBOARD_COLORS).map((c) => ({ hex: c.hex, ...(c.name?.trim() ? { name: c.name.trim() } : {}) }));
